// src/Roster.js

import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { collection, onSnapshot } from 'firebase/firestore';
import useDocumentMeta from './useDocumentMeta';
import { db } from './firebase';
import useRosterCatalog from './hooks/useRosterCatalog';
import {
  ROSTER_STATS_CSV_URL,
  mergePlayerWithOverride,
  shareRosterPlayerLink,
} from './utils/rosterCatalog';
import './Roster.css';

import placeholder from './assets/images/generic-player.png';

const Roster = () => {
  useDocumentMeta({
    title: 'Roster - Glass City Gladiators',
    description: 'Meet the 2013 & 2014 Glass City Gladiators teams: players, positions, bios and up-to-date season stats.',
    canonical: 'https://gcghockey.com/roster',
    ogImage: 'https://gcghockey.com/assets/og-roster.png',
    ogType: 'website',
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const [team, setTeam] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [stats, setStats] = useState({});
  const [overridesById, setOverridesById] = useState({});
  const [shareMessage, setShareMessage] = useState('');
  const { players2013, players2014 } = useRosterCatalog();

  useEffect(() => {
    fetch(ROSTER_STATS_CSV_URL)
      .then((res) => res.text())
      .then((csv) => {
        const rows = csv.split('\n');
        const data = {};
        for (let i = 1; i < rows.length; i++) {
          const cols = rows[i].split(',');
          const key = cols[0]?.trim().toLowerCase();
          if (!key) continue;
          data[key] = {
            goals: cols[1] || '0',
            assists: cols[2] || '0',
            points: cols[3] || '0',
            pims: cols[4] || '0',
          };
        }
        setStats(data);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'rosterOverrides'),
      (snap) => {
        const map = {};
        snap.forEach((d) => {
          map[d.id] = d.data();
        });
        setOverridesById(map);
      },
      (err) => console.error(err)
    );
    return () => unsub();
  }, []);

  const merged2013 = useMemo(
    () => players2013.map((p) => mergePlayerWithOverride(p, overridesById[p.id])),
    [overridesById, players2013]
  );

  const merged2014 = useMemo(
    () => players2014.map((p) => mergePlayerWithOverride(p, overridesById[p.id])),
    [overridesById, players2014]
  );

  // Keep open panel in sync when Firestore overrides / roster arrive
  useEffect(() => {
    if (!selectedPlayer || !selectedTeamId) return;
    const list = selectedTeamId === '2013' ? merged2013 : merged2014;
    const match = list.find((p) => p.name === selectedPlayer.name || p.id === selectedPlayer.id);
    if (!match) return;
    if (
      match.photoUrl !== selectedPlayer.photoUrl ||
      match.bio !== selectedPlayer.bio ||
      match.funFact !== selectedPlayer.funFact ||
      match.photoPosition !== selectedPlayer.photoPosition ||
      match.number !== selectedPlayer.number ||
      match.position !== selectedPlayer.position
    ) {
      setSelectedPlayer(match);
    }
  }, [merged2013, merged2014, selectedTeamId, selectedPlayer]);

  // Deep link: ?team=2014&player=Aiden%20Lenz
  useEffect(() => {
    const teamParam = searchParams.get('team');
    const playerParam = searchParams.get('player');
    if (!teamParam || (teamParam !== '2013' && teamParam !== '2014')) return;

    setTeam(teamParam);
    if (!playerParam) return;

    const list = teamParam === '2013' ? merged2013 : merged2014;
    const match = list.find((p) => p.name.toLowerCase() === playerParam.trim().toLowerCase());
    if (match) {
      setSelectedPlayer(match);
      setSelectedTeamId(teamParam);
    }
  }, [searchParams, merged2013, merged2014]);

  const openPlayer = (player, teamId) => {
    setSelectedPlayer(player);
    setSelectedTeamId(teamId);
    setShareMessage('');
    setSearchParams({ team: teamId, player: player.name });
  };

  const closePlayer = () => {
    setSelectedPlayer(null);
    setSelectedTeamId(null);
    setShareMessage('');
    const next = new URLSearchParams(searchParams);
    next.delete('player');
    if (team) next.set('team', team);
    setSearchParams(next);
  };

  const handleShare = async () => {
    if (!selectedPlayer || !selectedTeamId) return;
    const result = await shareRosterPlayerLink({
      teamId: selectedTeamId,
      playerName: selectedPlayer.name,
    });
    if (result.aborted) return;
    if (result.ok && result.method === 'clipboard') {
      setShareMessage('Link copied to clipboard!');
    } else if (!result.ok) {
      setShareMessage('Share not supported. Copy the URL from your browser.');
    } else {
      setShareMessage('');
    }
  };

  const renderGrid = (list, teamId) => (
    <div className="roster-players-grid">
      {list.map((player, idx) => (
        <div
          key={player.id || `${teamId}-${player.name}-${idx}`}
          className="roster-player-card"
          onClick={() => openPlayer(player, teamId)}
        >
          <div className="roster-player-number-badge">{player.number}</div>
          <div className="roster-player-image-container">
            <img
              src={player.hasPhotoPermission ? player.photoUrl : placeholder}
              alt={player.name}
              className={`roster-player-image ${player.position === 'Goalie' ? 'photo-shift-left' : ''}`}
              style={player.photoPosition ? { objectPosition: player.photoPosition } : {}}
            />
          </div>
          <div className="roster-player-info">
            <h3 className="roster-player-name">{player.name}</h3>
            <p className="roster-player-position">{player.position}</p>
          </div>
          <div className="roster-view-stats-hint">Click for stats →</div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="roster-container">
      <div className="ice-texture-bg"></div>

      <div className="roster-header">
        <h1 className="roster-title">Our Gladiators Roster</h1>
        <div className="title-underline"></div>
      </div>

      <div className="team-selector">
        <button
          className={`btn-team ${team === '2014' ? 'active' : ''}`}
          onClick={() => {
            setTeam('2014');
            setSearchParams({ team: '2014' });
          }}
        >
          14U
        </button>
        <button
          className={`btn-team ${team === '2013' ? 'active' : ''}`}
          onClick={() => {
            setTeam('2013');
            setSearchParams({ team: '2013' });
          }}
        >
          12U
        </button>
      </div>

      {team === '2014' && renderGrid(merged2014, '2014')}
      {team === '2013' && renderGrid(merged2013, '2013')}

      {selectedPlayer && (
        <>
          <div className="overlay" onClick={closePlayer}></div>
          <div className="player-detail-panel">
            <button className="close-panel" onClick={closePlayer}>
              ✕
            </button>

            <div className="panel-header">
              <div className="panel-number">{selectedPlayer.number}</div>
              <div className="panel-player-info">
                <h2>{selectedPlayer.name}</h2>
                <p className="panel-position">{selectedPlayer.position}</p>
              </div>
            </div>

            <div className="panel-image">
              <img
                src={selectedPlayer.hasPhotoPermission ? selectedPlayer.photoUrl : placeholder}
                alt={selectedPlayer.name}
                className={selectedPlayer.position === 'Goalie' ? 'photo-shift-left' : ''}
                style={selectedPlayer.photoPosition ? { objectPosition: selectedPlayer.photoPosition } : {}}
              />
            </div>

            <div className="panel-bio">
              <h3>About</h3>
              <p>{selectedPlayer.bio}</p>
            </div>

            {selectedPlayer.funFact ? (
              <div className="panel-fun-fact">
                <h3>Fun fact</h3>
                <p>{selectedPlayer.funFact}</p>
              </div>
            ) : null}

            <div className="panel-stats">
              <h3>Season Stats</h3>
              <div className="stats-grid">
                <div className="stat-box">
                  <div className="stat-value">{stats[selectedPlayer.name.toLowerCase()]?.goals || '0'}</div>
                  <div className="stat-label">Goals</div>
                </div>
                <div className="stat-box">
                  <div className="stat-value">{stats[selectedPlayer.name.toLowerCase()]?.assists || '0'}</div>
                  <div className="stat-label">Assists</div>
                </div>
                <div className="stat-box">
                  <div className="stat-value">{stats[selectedPlayer.name.toLowerCase()]?.points || '0'}</div>
                  <div className="stat-label">Points</div>
                </div>
                <div className="stat-box">
                  <div className="stat-value">{stats[selectedPlayer.name.toLowerCase()]?.pims || '0'}</div>
                  <div className="stat-label">PIMs</div>
                </div>
              </div>
            </div>

            <div className="panel-share-row">
              <button type="button" className="roster-share-btn" onClick={handleShare}>
                Share player profile
              </button>
              {shareMessage ? <p className="roster-share-msg">{shareMessage}</p> : null}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Roster;
